import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ExecutorType,
  UpdateExecutorType
} from 'apps/user/types/on-boarding.type';
import { AxiosError } from 'axios';
import { Alert } from 'components/Toast';
import { toast } from 'react-toastify';
import { services } from 'service';
import { Executor } from 'service/dto/executor.dto';

interface IProps {
  update: UpdateExecutorType;
  executorId: number;
}

export const useCreateExecutor = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (executor: ExecutorType): Promise<void> => {
      return services.executorService.createExecutor(executor);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['create-executor']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useUpdateExecutor = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ executorId, update }: IProps): Promise<void> => {
      return services.executorService.updateExecutor(executorId, update);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['update-trustee']);
      },
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );
};

export const useGetExecutorList = ({
  enabled = true,
  search = undefined
}: {
  search?: string;
  enabled?: boolean;
} = {}) => {
  const searchQuery = search && search?.length >= 3 ? search : '';
  return useQuery(
    ['executors'],
    () => services.executorService.getExecutors(searchQuery),
    {
      refetchOnWindowFocus: false,
      enabled: enabled
    }
  );
};

export const useDeleteExecutor = () =>
  useMutation(
    (executorId: number) => services.executorService.deleteExecutor(executorId),
    {
      onError: (error: Error) => {
        toast.error(<Alert message={error.message} type="error" />);
      }
    }
  );

export const useGetExecutor = ({ executorId }: { executorId: number }) => {
  return useQuery<Executor, AxiosError<Error>>(
    ['get_executor', executorId],
    () => services.executorService.getExecutor(executorId),
    {
      keepPreviousData: false,
      refetchOnWindowFocus: false,
      enabled: !!executorId
    }
  );
};
